import { create } from 'zustand';
import type { NextClientResponse } from '@/types/api';

/**
 * Volatile (in-memory only) store for the agent's currently-active claim.
 *
 * Filled when "take next" succeeds, consumed by PreCallPage / ScreeningPage,
 * cleared when the screening is submitted or the user navigates back to queue.
 *
 * Not persisted: this carries decrypted PHI and we want it to vanish on
 * tab close / logout.
 */
interface ActiveCallState {
  current: NextClientResponse | null;
  /** screening id once start has been called (so screening pages can reach it) */
  activeScreeningId: string | null;

  // Cluster H — inline sub-screening tracking (drawer open over the parent).
  // Set by SubScreeningDrawer when promote-and-screen returns; cleared on
  // submit/abandon/drawer-close. Independent from `activeScreeningId` (which
  // tracks the PARENT screening); both can coexist while the drawer is open.
  currentSubScreeningId: string | null;
  currentSubClientId: string | null;
  currentSubAssignmentId: string | null;

  setCurrent: (data: NextClientResponse) => void;
  setActiveScreeningId: (id: string) => void;
  setCurrentSub: (data: {
    subScreeningId: string;
    subClientId: string;
    subAssignmentId: string;
  }) => void;
  clearCurrentSub: () => void;
  clear: () => void;
}

export const useActiveCallStore = create<ActiveCallState>((set) => ({
  current: null,
  activeScreeningId: null,
  currentSubScreeningId: null,
  currentSubClientId: null,
  currentSubAssignmentId: null,
  // HIPAA hotfix 2026-05-13 — A-8: when take-next swaps the active claim, also
  // clear activeScreeningId AND the sub-screening fields so the screening
  // id from a PREVIOUS client doesn't bleed into the new pre-call/screening
  // flow. Cross-client PHI leak. Cluster H 2026-05-17 extends this to subs.
  setCurrent: (data) =>
    set({
      current: data,
      activeScreeningId: null,
      currentSubScreeningId: null,
      currentSubClientId: null,
      currentSubAssignmentId: null,
    }),
  setActiveScreeningId: (id) => set({ activeScreeningId: id }),
  setCurrentSub: ({ subScreeningId, subClientId, subAssignmentId }) =>
    set({
      currentSubScreeningId: subScreeningId,
      currentSubClientId: subClientId,
      currentSubAssignmentId: subAssignmentId,
    }),
  clearCurrentSub: () =>
    set({
      currentSubScreeningId: null,
      currentSubClientId: null,
      currentSubAssignmentId: null,
    }),
  clear: () =>
    set({
      current: null,
      activeScreeningId: null,
      currentSubScreeningId: null,
      currentSubClientId: null,
      currentSubAssignmentId: null,
    }),
}));
